import { Button, Group, Stack, Tooltip } from '@mantine/core';
import { useField } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { TSDListItem } from '@e-origin/contracts';

import { TextInput } from '@components';
import { useDuplicateTSD } from '@hooks/tsd';

interface DuplicateTSDProps {
  tsd: TSDListItem;
  onHide(): void;
}

const nameSchema = z
  .string()
  .min(1, { message: 'Required' })
  .regex(/^[a-zA-Z0-9-]*$/g, { message: 'Name is invalid' });

export const DuplicateTSD: React.FC<DuplicateTSDProps> = ({ tsd, onHide }) => {
  const duplicateTSDMutation = useDuplicateTSD();

  const nameField = useField({
    initialValue: '',
    validate: (value) => (nameSchema.safeParse(value).success ? null : 'Name is invalid'),
    validateOnBlur: true,
  });

  useEffect(() => {
    if (tsd) {
      const prefix = 'DUPL';
      nameField.setValue(`${prefix}-${tsd.name}`);
    }
  }, [tsd]);

  const handleDuplicate = async () => {
    await duplicateTSDMutation.mutateAsync({
      tsdId: tsd._id,
      name: nameField.getValue(),
    });

    onHide();
  };

  const isPending = duplicateTSDMutation.isPending;
  const isValid = !nameField.error;

  return (
    <Stack>
      <TextInput {...nameField.getInputProps()} label="Temporary Storage name" size="lg" data-pw="duplicate-temporary-storage-name-input" w="100%" />

      <Group justify="center" mt="xl">
        <Button type="button" onClick={onHide} disabled={isPending} size="lg" variant="transparent">
          Cancel
        </Button>

        <Tooltip label="Duplicate">
          <Button type="button" onClick={handleDuplicate} loading={isPending} disabled={!isValid} variant="filled" size="lg">
            Duplicate
          </Button>
        </Tooltip>
      </Group>
    </Stack>
  );
};
